/**
 * Unified Enrichment Manager
 * Consolidated dashboard for AI enrichment, web scraping, and social media capture
 * across all data types (Providers, CBOs, Programs)
 *
 * Design: Tailwind + Lucide icons (matching Provider Import Manager)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles,
  Globe,
  Users,
  RefreshCw,
  Play,
  CheckCircle,
  Clock,
  AlertCircle,
  Activity,
  Database,
  // Building2,
  FileText,
  TrendingUp,
  Zap,
  Search,
  MapPin,
  Navigation,
  Phone,
  Mail
} from 'lucide-react';
import { config } from '@/src/config';
import CBOProviderRefinement from './CBOProviderRefinement';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
// Use proxy path in production to avoid CORS issues
// The proxy /provider-api/* maps to https://provider-directory-api.onrender.com/api/*
// So /provider-api/import/cbo/stats -> https://provider-directory-api.onrender.com/api/import/cbo/stats
const getApiBaseUrl = () => {
  // Desktop (Tauri) has no Vercel proxy, so the relative /provider-api path
  // won't resolve — hit the absolute service URL directly, same as dev.
  if (config.isProduction && !config.isDesktop) {
    // Don't add /api here - the proxy already maps to /api
    return '/provider-api';
  }
  // For dev/local/desktop, use the full base URL which includes /api
  return config.providerApi.baseUrl;
};

const API_BASE_URL = getApiBaseUrl();

interface Stats {
  providers: {
    total: number;
    pending_ai: number;
    enriched: number;
    failed: number;
  };
  cbos: {
    total: number;
    pending_ai: number;
    enriched: number;
    failed: number;
    missing_phone?: number;
    missing_email?: number;
    missing_website?: number;
    geocoded?: number;
    pending_geocode?: number;
  };
  programs: {
    total: number;
    pending_ai: number;
    enriched: number;
    failed: number;
  };
}

// Geocoding stats interface
interface GeocodingStats {
  status_counts: Record<string, number>;
  pending_geocode: number;
  geocoded: number;
  pending_by_state: Record<string, number>;
  recent_jobs: Array<{
    id: number;
    status: string;
    started_at: string | null;
    completed_at: string | null;
    total_processed: number;
    geocoded_count: number;
    failed_count: number;
  }>;
}

// Geocoding job interface
interface GeocodingJob {
  id: number;
  status: string;
  batch_size?: number;
  max_records?: number;
  state_filter?: string;
  started_at?: string;
  completed_at?: string;
  total_processed?: number;
  geocoded_count?: number;
  failed_count?: number;
  po_box_count?: number;
  state_fallback_count?: number;
  error_message?: string;
  created_at?: string;
}

const UnifiedEnrichmentManager: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [activeSection, setActiveSection] = useState<'pipeline' | 'ai-approval' | 'settings'>('pipeline');
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Stats
  const [stats, setStats] = useState<Stats | null>(null);

  // Form state
  const [dataType, setDataType] = useState<'provider' | 'cbo' | 'program'>('cbo');
  const [stateFilter, setStateFilter] = useState('');
  const [batchSize, setBatchSize] = useState(50);

  // Auto-approval state
  const [autoApprovalThreshold, setAutoApprovalThreshold] = useState(0.85);
  const [autoApprovalStats, setAutoApprovalStats] = useState<any>(null);

  // Geocoding state
  const [geocodingStats, setGeocodingStats] = useState<GeocodingStats | null>(null);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [markingForGeocoding, setMarkingForGeocoding] = useState(false);
  const [activeGeocodingJob, setActiveGeocodingJob] = useState<GeocodingJob | null>(null);
  const [geocodingMaxRecords, setGeocodingMaxRecords] = useState(1000);
  const [geocodingPollingInterval, setGeocodingPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  // US States
  const US_STATES = [
    'CA', 'TX', 'FL', 'NY', 'PA', 'IL', 'OH', 'GA', 'NC', 'MI',
    'NJ', 'VA', 'WA', 'AZ', 'MA', 'TN', 'IN', 'MD', 'MO', 'WI',
    'CO', 'MN', 'SC', 'AL', 'LA', 'KY', 'OR', 'OK', 'CT', 'UT'
  ];

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      // Fetch CBO stats
      const cboResponse = await fetch(`${API_BASE_URL}/import/cbo/stats`);
      const cboData = cboResponse.ok ? await cboResponse.json() : null;

      const bySource = cboData?.by_source || {};
      const irsCount = bySource['irs_bulk'] || 0;
      const cmsCount = bySource['CMS_NPPES'] || 0;
      setStats({
        providers: {
          total: cmsCount + (bySource['HRSA'] || 0),
          pending_ai: 0,
          enriched: 0,
          failed: 0,
        },
        cbos: {
          total: cboData?.total_providers || 0,
          pending_ai: cboData?.pending_enrichment || 0,
          enriched: cboData?.enriched || 0,
          failed: cboData?.enrichment_failed || 0,
          missing_phone: cboData?.missing_phone || 0,
          missing_email: cboData?.missing_email || 0,
          missing_website: cboData?.missing_website || 0,
          geocoded: cboData?.geocoded || 0,
          pending_geocode: cboData?.pending_geocode || 0,
        },
        programs: {
          total: 0,
          pending_ai: 0,
          enriched: 0,
          failed: 0,
        },
        // Extended enrichment metrics from /cbo/stats
        _with_phone: cboData?.with_phone || 0,
        _with_website: cboData?.with_website || 0,
        _with_email: cboData?.with_email || 0,
        _tier1_enriched: cboData?.tier1_enriched || 0,
        _tier2_enriched: cboData?.tier2_enriched || 0,
        _by_source_irs: irsCount,
        _by_source_cms: cmsCount,
      } as any);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch auto-approval stats
  const fetchAutoApprovalStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/enrichments/auto-approve/stats`);
      if (response.ok) {
        const data = await response.json();
        setAutoApprovalStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch auto-approval stats:', err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Run AI Enrichment
  const handleAIEnrichment = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let url = '';
      if (dataType === 'cbo') {
        url = `${API_BASE_URL}/import/cbo/enrich/batch?limit=${batchSize}`;
        if (stateFilter) url += `&state=${stateFilter}`;
      } else {
        setError('AI enrichment for this data type is not yet implemented');
        setLoading(false);
        return;
      }

      const response = await fetch(url, { method: 'POST' });
      const result = await response.json();

      if (response.ok) {
        setSuccess(`AI Enrichment completed! Enriched: ${result.enriched || 0}, Failed: ${result.failed || 0}`);
        await fetchStats();
      } else {
        setError(result.detail || 'AI enrichment failed');
      }
    } catch (err) {
      setError('Failed to run AI enrichment: ' + String(err));
    } finally {
      setLoading(false);
    }
  };

  // Run Web Scraping
  const handleWebScraping = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let url = '';
      if (dataType === 'cbo') {
        url = `${API_BASE_URL}/import/cbo/scrape/batch?limit=${batchSize}`;
        if (stateFilter) url += `&state=${stateFilter}`;
      } else {
        setError('Web scraping for this data type is not yet implemented');
        setLoading(false);
        return;
      }

      const response = await fetch(url, { method: 'POST' });
      const result = await response.json();

      if (response.ok) {
        // Backend returns 'enriched' not 'scraped', also show total for debugging
        const enriched = result.enriched || result.scraped || 0;
        const total = result.total || 0;
        const skipped = result.skipped || 0;
        setSuccess(`Web scraping completed! Total: ${total}, Scraped: ${enriched}, Skipped: ${skipped}, Failed: ${result.failed || 0}`);
        if (total === 0) {
          setError('No providers found to scrape. Check if CBOs exist in the database with websites that need scraping.');
        }
        await fetchStats();
      } else {
        setError(result.detail || result.message || 'Web scraping failed');
      }
    } catch (err) {
      setError('Failed to run web scraping: ' + String(err));
    } finally {
      setLoading(false);
    }
  };

  // Run Social Media Capture
  const handleSocialCapture = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let url = '';
      if (dataType === 'cbo') {
        url = `${API_BASE_URL}/import/cbo/social/batch?limit=${batchSize}`;
        if (stateFilter) url += `&state=${stateFilter}`;
      } else {
        setError('Social media capture for this data type is not yet implemented');
        setLoading(false);
        return;
      }

      const response = await fetch(url, { method: 'POST' });
      const result = await response.json();

      if (response.ok) {
        // Backend returns 'enriched' not 'captured', also show total for debugging
        const enriched = result.enriched || result.captured || 0;
        const total = result.total || 0;
        const skipped = result.skipped || 0;
        setSuccess(`Social media capture completed! Total: ${total}, Enriched: ${enriched}, Skipped: ${skipped}, Failed: ${result.failed || 0}`);

        // Show API key status and errors
        const errors = result.errors || [];
        const apiKeys = result.api_keys_configured;
        if (total === 0) {
          setError('No providers found to enrich. Check if CBOs exist in the database with missing social media data.');
        } else if (errors.length > 0) {
          setError(`API Status: ${errors.join(', ')}${apiKeys ? ` | Google: ${apiKeys.google ? '✓' : '✗'}, Yelp: ${apiKeys.yelp ? '✓' : '✗'}` : ''}`);
        }
        await fetchStats();
      } else {
        setError(result.detail || result.message || 'Social media capture failed');
      }
    } catch (err) {
      setError('Failed to run social media capture: ' + String(err));
    } finally {
      setLoading(false);
    }
  };

  // Run Contact Discovery Waterfall
  // Waterfall state
  const [waterfallMaxTier, setWaterfallMaxTier] = useState(3);
  const [waterfallLimit, setWaterfallLimit] = useState(5000);
  const [waterfallBraveBudget, setWaterfallBraveBudget] = useState(25);
  const [waterfallHunterBudget, setWaterfallHunterBudget] = useState(50);
  const [enrichmentJobs, setEnrichmentJobs] = useState<any[]>([]);

  // Form 990 state
  const [form990Year, setForm990Year] = useState(2023);
  const [form990Batches, setForm990Batches] = useState(10);

  const fetchEnrichmentJobs = useCallback(async () => {
    try {
      // Merge online/triggered import jobs (provider_import_jobs) with the
      // Tier 2 cron runs (enrichment_jobs) so the ETL pipeline view shows the
      // background Brave+scrape cron as it runs, not just manual jobs.
      const [importResp, tier2Resp] = await Promise.all([
        fetch(`${API_BASE_URL}/import/cbo/import/jobs?limit=20`).catch(() => null),
        fetch(`${API_BASE_URL}/import/cbo/enrichment/jobs?limit=20`).catch(() => null),
      ]);

      let jobs: any[] = [];
      if (importResp && importResp.ok) {
        const data = await importResp.json();
        jobs = (data.jobs || []).filter((j: any) =>
          ['contact_waterfall', 'form990_enrichment', 'geocoding', 'ntee_enrichment', 'enrichment'].includes(j.source_type || j.source_name)
        );
      }
      if (tier2Resp && tier2Resp.ok) {
        const t2 = await tier2Resp.json();
        jobs = [...(t2.jobs || []), ...jobs];
      }

      // Sort newest-first across both sources.
      const ts = (j: any) => new Date(j.updated_at || j.completed_at || j.started_at || j.created_at || 0).getTime();
      jobs.sort((a, b) => ts(b) - ts(a));
      setEnrichmentJobs(jobs);
    } catch { /* ignore */ }
  }, []);

  // Load the ETL pipeline job list once when entering the pipeline tab. No
  // polling — the periodic auto-refresh caused the page to re-render/loop and
  // made navigation difficult; metrics live on the Analytics tab now.
  useEffect(() => {
    if (activeSection === 'pipeline') fetchEnrichmentJobs();
  }, [activeSection, fetchEnrichmentJobs]);

  const handleContactDiscovery = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const params = new URLSearchParams({
        max_tier: String(waterfallMaxTier),
        limit: String(waterfallLimit),
        tier2_budget: String(waterfallBraveBudget),
        tier3_budget: String(waterfallHunterBudget),
      });
      if (stateFilter) params.set('state', stateFilter);

      const response = await fetch(`${API_BASE_URL}/import/cbo/enrich/contact-waterfall?${params}`, { method: 'POST' });
      const text = await response.text();
      let result: any;
      try {
        result = JSON.parse(text);
      } catch {
        setError(`Server error: ${text.substring(0, 200)}`);
        setLoading(false);
        return;
      }

      if (response.ok) {
        setSuccess(`Waterfall job #${result.job_id} dispatched — processing ${waterfallLimit.toLocaleString()} providers (Tier 1-${waterfallMaxTier})`);
        await fetchStats();
        await fetchEnrichmentJobs();
      } else {
        setError(result.detail || result.error || 'Contact discovery failed');
      }
    } catch (err) {
      setError('Failed to run contact discovery: ' + String(err));
    } finally {
      setLoading(false);
    }
  };

  // Run Form 990 enrichment
  const handleForm990 = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/import/cbo/enrich/form990?year=${form990Year}&max_batches=${form990Batches}`,
        { method: 'POST' }
      );
      const result = await response.json();
      if (response.ok) {
        setSuccess(`Form 990 job #${result.job_id} dispatched — processing ${form990Year} filings (${form990Batches} batches)`);
        await fetchEnrichmentJobs();
      } else {
        setError(result.detail || result.error || 'Form 990 enrichment failed');
      }
    } catch (err) {
      setError('Failed to start Form 990 enrichment: ' + String(err));
    } finally {
      setLoading(false);
    }
  };

  // Run Auto-Approval
  const handleAutoApproval = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const url = `${API_BASE_URL}/import/cbo/enrichments/auto-approve?confidence_threshold=${autoApprovalThreshold}&limit=${batchSize}`;
      const response = await fetch(url, { method: 'POST' });
      const result = await response.json();

      if (response.ok) {
        setSuccess(`Auto-approval completed! Approved: ${result.approved || 0}, Skipped: ${result.skipped || 0}`);
        await fetchStats();
        await fetchAutoApprovalStats();
      } else {
        setError(result.detail || 'Auto-approval failed');
      }
    } catch (err) {
      setError('Failed to run auto-approval: ' + String(err));
    } finally {
      setLoading(false);
    }
  };

  // Fetch geocoding statistics.
  // Uses /geocoding/progress (reads the correct geocoding_status column) rather
  // than /geocoding/stats, whose underlying get_geocoding_statistics() reads the
  // stale ai_enrichment_status column and reported ~0/60k instead of the real
  // count. Maps the progress response into the GeocodingStats shape the panel
  // expects so it reflects the background geocoder as it happens.
  const fetchGeocodingStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/geocoding/progress`);
      if (response.ok) {
        const data = await response.json();
        setGeocodingStats({
          status_counts: data.status_counts || {},
          pending_geocode: data.pending ?? data.pending_geocode ?? 0,
          geocoded: data.geocoded ?? 0,
          pending_by_state: data.pending_by_state || {},
          recent_jobs: data.job ? [data.job] : [],
        });
      }
    } catch (err) {
      console.error('Failed to fetch geocoding stats:', err);
    }
  }, []);

  // Load geocoding stats once when entering Settings — no polling (avoids the
  // page-looping refresh). Full geocoding/coverage metrics live on Analytics.
  useEffect(() => {
    if (activeSection === 'settings') fetchGeocodingStats();
  }, [activeSection, fetchGeocodingStats]);

  // Start geocoding job
  const handleStartGeocoding = async () => {
    setGeocodingLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let url = `${API_BASE_URL}/import/cbo/geocoding/start?batch_size=100&max_records=${geocodingMaxRecords}`;
      if (stateFilter) {
        url += `&state_filter=${stateFilter}`;
      }

      const response = await fetch(url, { method: 'POST' });
      const result = await response.json();

      if (response.ok) {
        // Set active job with running status (job_id may be null if table doesn't exist)
        setActiveGeocodingJob({
          id: result.job_id || 0,
          status: 'RUNNING',
          max_records: geocodingMaxRecords,
          total_processed: 0,
          geocoded_count: 0
        });

        if (result.job_id) {
          setSuccess(`Geocoding job #${result.job_id} started! Processing up to ${geocodingMaxRecords.toLocaleString()} records.`);
          // Poll specific job
          const interval = setInterval(() => pollGeocodingJobById(result.job_id), 3000);
          setGeocodingPollingInterval(interval);
        } else {
          setSuccess(`Geocoding started in background! Processing up to ${geocodingMaxRecords.toLocaleString()} records.`);
          // Poll active job endpoint when no job_id (fallback for when geocoding_jobs table doesn't exist)
          const interval = setInterval(() => pollActiveGeocodingJob(), 3000);
          setGeocodingPollingInterval(interval);
        }

        // Also refresh stats periodically
        const statsInterval = setInterval(() => fetchGeocodingStats(), 5000);

        // Stop polling after 10 minutes max
        setTimeout(() => {
          if (geocodingPollingInterval) clearInterval(geocodingPollingInterval);
          clearInterval(statsInterval);
          setGeocodingPollingInterval(null);
          setActiveGeocodingJob(null);
          fetchGeocodingStats();
        }, 600000);
      } else {
        setError(result.detail || result.message || 'Failed to start geocoding job');
      }
    } catch (err) {
      setError('Failed to start geocoding: ' + String(err));
    } finally {
      setGeocodingLoading(false);
    }
  };

  // Poll active geocoding job (when job_id is not available)
  const pollActiveGeocodingJob = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/geocoding/active`);
      if (response.ok) {
        const data = await response.json();

        if (data.has_active_job && data.job) {
          setActiveGeocodingJob({
            id: data.job.id,
            status: data.job.status,
            max_records: data.job.max_records,
            total_processed: data.job.total_processed,
            geocoded_count: data.job.geocoded_count,
            failed_count: data.job.failed_count,
            po_box_count: data.job.po_box_count,
            state_fallback_count: data.job.state_fallback_count,
          });
        } else if (data.last_completed) {
          // Job finished
          if (geocodingPollingInterval) {
            clearInterval(geocodingPollingInterval);
            setGeocodingPollingInterval(null);
          }

          const job = data.last_completed;
          if (job.status === 'COMPLETED') {
            setSuccess(`Geocoding completed! Geocoded: ${job.geocoded_count?.toLocaleString() || 0}, Failed: ${job.failed_count || 0}`);
          } else {
            setError(`Geocoding failed: ${job.error_message || 'Check logs'}`);
          }
          setActiveGeocodingJob(null);
          await fetchGeocodingStats();
        }
      }
    } catch (err) {
      console.error('Failed to poll active geocoding:', err);
    }
  };

  // Mark providers for geocoding (converts 'pending' to 'pending_geocode')
  const handleMarkForGeocoding = async () => {
    setMarkingForGeocoding(true);
    setError(null);
    setSuccess(null);

    try {
      const url = `${API_BASE_URL}/import/cbo/geocoding/mark-pending?max_records=${geocodingMaxRecords}`;
      const response = await fetch(url, { method: 'POST' });
      const result = await response.json();

      if (response.ok) {
        setSuccess(`Marked ${result.marked?.toLocaleString() || 0} providers for geocoding. You can now run geocoding.`);
        await fetchGeocodingStats();
      } else {
        setError(result.detail || result.message || 'Failed to mark providers for geocoding');
      }
    } catch (err) {
      setError('Failed to mark providers: ' + String(err));
    } finally {
      setMarkingForGeocoding(false);
    }
  };

  // Poll geocoding job status by job ID (from database)
  const pollGeocodingJobById = async (jobId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/geocoding/jobs?limit=10`);
      if (response.ok) {
        const jobs = await response.json();
        // Find our job in the list
        const job = Array.isArray(jobs) ? jobs.find((j: GeocodingJob) => j.id === jobId) : null;

        if (job) {
          setActiveGeocodingJob(job);

          // Check if job completed or failed
          if (job.status === 'COMPLETED' || job.status === 'FAILED') {
            // Stop polling
            if (geocodingPollingInterval) {
              clearInterval(geocodingPollingInterval);
              setGeocodingPollingInterval(null);
            }

            // Update stats
            await fetchGeocodingStats();

            if (job.status === 'COMPLETED') {
              setSuccess(`Geocoding completed! Geocoded: ${job.geocoded_count?.toLocaleString() || 0}, PO Box: ${job.po_box_count?.toLocaleString() || 0}, Fallbacks: ${job.state_fallback_count?.toLocaleString() || 0}`);
            } else if (job.status === 'FAILED') {
              setError(`Geocoding job failed: ${job.error_message || 'Check logs for details.'}`);
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to poll geocoding status:', err);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (geocodingPollingInterval) {
        clearInterval(geocodingPollingInterval);
      }
    };
  }, [geocodingPollingInterval]);

  // Get stats for current data type
  const getCurrentStats = () => {
    if (!stats) return null;
    switch (dataType) {
      case 'provider': return stats.providers;
      case 'cbo': return stats.cbos;
      case 'program': return stats.programs;
      default: return null;
    }
  };

  const currentStats = getCurrentStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('admin.unified_enrichment_manager', 'Unified Enrichment Manager')}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.ai_enrichment_web_scraping_and', 'AI enrichment, contact discovery, web scraping, and social media across all data types')}
                </p>
              </div>
            </div>
            <button
              onClick={fetchStats}
              disabled={statsLoading}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${statsLoading ? 'animate-spin' : ''}`} />
              {t('admin.refresh', 'Refresh')}
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              <button onClick={() => setError(null)} className="text-xs text-red-600 hover:underline mt-1">{t('admin.dismiss', 'Dismiss')}</button>
            </div>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
              <button onClick={() => setSuccess(null)} className="text-xs text-green-600 hover:underline mt-1">{t('admin.dismiss_1', 'Dismiss')}</button>
            </div>
          </div>
        )}

        {/* Data Type and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.data_type', 'Data Type')}
              </label>
              <select
                value={dataType}
                onChange={(e) => setDataType(e.target.value as 'provider' | 'cbo' | 'program')}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="provider">{t('admin.healthcare_providers', 'Healthcare Providers')}</option>
                <option value="cbo">Community Organizations (CBOs)</option>
                <option value="program">{t('admin.programs', 'Programs')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.state_filter', 'State Filter')}
              </label>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">{t('admin.all_states', 'All States')}</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.batch_size', 'Batch Size')}
              </label>
              <input
                type="number"
                value={batchSize}
                onChange={(e) => setBatchSize(Math.min(500, Math.max(10, parseInt(e.target.value) || 50)))}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min="10"
                max="500"
              />
            </div>
            <div className="flex items-end">
              {statsLoading ? (
                <div className="w-full h-10 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
              ) : currentStats && (
                <div className="w-full px-3 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-xs text-purple-600 dark:text-purple-400">{t('admin.pending_enrichment', 'Pending Enrichment')}</p>
                  <p className="text-lg font-bold text-purple-900 dark:text-purple-100">{currentStats.pending_ai.toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coverage + enrichment-tier metrics moved to the Analytics tab
            (single metrics surface; the auto-refreshing bar here caused the
            page to loop and made navigation difficult). */}

        {/* Tabs — consolidated from 7 to 3 */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
            <div className="flex w-max min-w-full">
              <Tab
                label="Enrichment Pipeline"
                icon={<Activity className="h-4 w-4" />}
                active={activeSection === 'pipeline'}
                onClick={() => {
                  setActiveSection('pipeline');
                  fetchEnrichmentJobs();
                  fetchGeocodingStats();
                }}
              />
              <Tab
                label="AI Enrichment & Approval"
                icon={<Sparkles className="h-4 w-4" />}
                active={activeSection === 'ai-approval'}
                onClick={() => {
                  setActiveSection('ai-approval');
                  fetchAutoApprovalStats();
                }}
              />
              <Tab
                label="Settings"
                icon={<Database className="h-4 w-4" />}
                active={activeSection === 'settings'}
                onClick={() => setActiveSection('settings')}
              />
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* TAB 1: ENRICHMENT PIPELINE — all tiers in one view           */}
        {/* ============================================================ */}
        {activeSection === 'pipeline' && (
          <div className="space-y-6">
            {/* Contact-gap counts (missing phone/email/website) were fetched once on
                mount and never refreshed, so they went stale as Tier 2 ran. Live
                coverage now lives on the Analytics tab (single metrics surface). */}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Waterfall Config & Launch */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="h-5 w-5 text-emerald-600" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Run Contact Discovery</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Dispatches a background Celery job that runs the full waterfall pipeline.
                </p>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Providers to Process</label>
                    <input type="number" value={waterfallLimit} onChange={e => setWaterfallLimit(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Tier</label>
                    <select value={waterfallMaxTier} onChange={e => setWaterfallMaxTier(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                      <option value={2}>Tier 2 — Brave Search + Scraping</option>
                      <option value={3}>Tier 3 — + Hunter.io Email</option>
                      <option value={4}>Tier 4 — + ZoomInfo (Premium)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Brave Budget ($)</label>
                      <input type="number" value={waterfallBraveBudget} onChange={e => setWaterfallBraveBudget(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Hunter Budget ($)</label>
                      <input type="number" value={waterfallHunterBudget} onChange={e => setWaterfallHunterBudget(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">State Filter (optional)</label>
                    <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                      <option value="">All States</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  onClick={handleContactDiscovery}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Dispatching...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Run Waterfall ({waterfallLimit.toLocaleString()} providers, Tier 1-{waterfallMaxTier})
                    </>
                  )}
                </button>
              </div>

              {/* Right: Form 990 + Tier Descriptions */}
              <div className="space-y-6">
                {/* Form 990 Trigger */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Form 990 XML Enrichment (Free)</h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Extract phone, website, mission, programs, and employee count from IRS 990 filings. Run multiple years to maximize coverage.
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Filing Year</label>
                      <select value={form990Year} onChange={e => setForm990Year(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                        <option value={2024}>2024 (Most Recent)</option>
                        <option value={2023}>2023</option>
                        <option value={2022}>2022</option>
                        <option value={2021}>2021</option>
                        <option value={2020}>2020</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max Batches</label>
                      <select value={form990Batches} onChange={e => setForm990Batches(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                        <option value={5}>5 batches (~50k filings)</option>
                        <option value={10}>10 batches (~100k filings)</option>
                        <option value={20}>20 batches (~200k filings)</option>
                        <option value={50}>All batches</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={handleForm990}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                  >
                    {loading ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Dispatching...</>
                    ) : (
                      <><FileText className="h-4 w-4" /> Run Form 990 ({form990Year})</>
                    )}
                  </button>
                </div>

                {/* Tier Descriptions */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Enrichment Pipeline</h3>
                  </div>
                  <div className="space-y-4">
                    <StepItem number={0} title="Tier 0 — NTEE Deterministic (Free)" description="Services and org type from NTEE codes. 100% coverage on IRS CBOs." />
                    <StepItem number={1} title="Tier 1 — Form 990 XML (Free)" description="Phone, website, mission, programs, employee count. Run years 2020-2024 for best coverage." />
                    <StepItem number={2} title="Tier 2 — Brave Search ($0.001/query)" description="Website discovery using org name + city + state. 59% discovery rate." />
                    <StepItem number={3} title="Tier 3 — Hunter.io ($0.01/query)" description="Email discovery from discovered website domains." />
                    <StepItem number={4} title="Tier 4 — ZoomInfo (Premium)" description="Verified contacts with phone, email, title. Requires API key + budget." />
                  </div>
                </div>
              </div>
            </div>

            {/* Enrichment Job History */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-gray-600" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Enrichment Job History</h3>
                </div>
                <button onClick={fetchEnrichmentJobs} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
                  <RefreshCw className="h-3 w-3" /> Refresh
                </button>
              </div>
              {enrichmentJobs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No enrichment jobs found. Click Refresh to load.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b dark:border-gray-700">
                        <th className="py-2 pr-3">Job</th>
                        <th className="py-2 pr-3">Type</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Progress</th>
                        <th className="py-2 pr-3">Results</th>
                        <th className="py-2">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichmentJobs.slice(0, 10).map((job: any) => {
                        const d = job.error_log || job.progress_detail || {};
                        const web = d.websites_discovered || d.enriched_website || 0;
                        const ph = d.phones_found || d.enriched_phone || 0;
                        const em = (d.emails_found || 0) + (d.tier3_enriched || 0);
                        const spent = (d.tier2_spent || 0) + (d.tier3_spent || 0);
                        const prog = d.progress || `${job.imported_records || 0}/${job.total_records || 0}`;
                        const typeName = (job.source_type || job.source_name || '').replace(/_/g, ' ');
                        const statusColor = job.status === 'COMPLETED' ? 'text-green-600' :
                          job.status === 'RUNNING' ? 'text-blue-600' :
                          job.status === 'FAILED' ? 'text-red-600' : 'text-gray-500';
                        return (
                          <tr key={job.id} className="border-b dark:border-gray-700">
                            <td className="py-2 pr-3 font-mono text-xs">#{job.id}</td>
                            <td className="py-2 pr-3 capitalize">{typeName}</td>
                            <td className={`py-2 pr-3 font-medium ${statusColor}`}>{job.status}</td>
                            <td className="py-2 pr-3 font-mono text-xs">{prog}</td>
                            <td className="py-2 pr-3 text-xs">
                              {web > 0 && <span className="mr-2">🌐 {web}</span>}
                              {ph > 0 && <span className="mr-2">📞 {ph.toLocaleString()}</span>}
                              {em > 0 && <span>✉️ {em}</span>}
                              {d.matched > 0 && <span>🔗 {d.matched.toLocaleString()} matched</span>}
                              {d.geocoded > 0 && <span>📍 {d.geocoded.toLocaleString()}</span>}
                            </td>
                            <td className="py-2 text-xs">{spent > 0 ? `$${spent.toFixed(2)}` : 'Free'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB 2: AI ENRICHMENT & APPROVAL                              */}
        {/* ============================================================ */}
        {activeSection === 'ai-approval' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* AI Enrichment */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">RAG Enrichment — ad-hoc batch</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Extracts services, categorization, and eligibility using the trained RAG model
                  (few-shot retrieval over human-approved enrichments). Bulk enrichment runs
                  automatically on a schedule — use this only for an ad-hoc or targeted batch.
                </p>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Records to Process</label>
                    <input type="number" value={batchSize} onChange={e => setBatchSize(Number(e.target.value))}
                      min={10} max={10000} step={50}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                    <p className="text-xs text-gray-500 mt-1">~${(batchSize * 0.002).toFixed(2)} estimated cost</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">State Filter (optional)</label>
                    <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm">
                      <option value="">All States</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  onClick={handleAIEnrichment}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  {loading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Enriching...</>
                  ) : (
                    <><Play className="h-4 w-4" /> Enrich {batchSize.toLocaleString()} Records</>
                  )}
                </button>
              </div>

              {/* Auto-Approval + Pending Review */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="h-5 w-5 text-amber-600" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Auto-Approval & Review</h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  High-confidence AI enrichments are auto-approved. Lower-confidence items are queued for manual review.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Confidence Threshold</label>
                    <select value={autoApprovalThreshold} onChange={e => setAutoApprovalThreshold(parseFloat(e.target.value))}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100">
                      <option value={0.95}>95% — Very Conservative</option>
                      <option value={0.90}>90% — Conservative</option>
                      <option value={0.85}>85% — Balanced (Recommended)</option>
                      <option value={0.80}>80% — Aggressive</option>
                    </select>
                  </div>

                  <button
                    onClick={handleAutoApproval}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                  >
                    {loading ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Processing...</>
                    ) : (
                      <><Zap className="h-4 w-4" /> Run Auto-Approval</>
                    )}
                  </button>
                </div>

                {autoApprovalStats && (
                  <div className="mt-4 grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Auto-approved (7d)</p>
                      <p className="text-lg font-bold text-green-600">{autoApprovalStats.last_7_days || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Pending Review</p>
                      <p className="text-lg font-bold text-amber-600">{autoApprovalStats.pending || 0}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Pending Review Queue (replaces old Refinement tab) */}
            <CBOProviderRefinement />
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB 3: SETTINGS — API keys, geocoding config, budgets        */}
        {/* ============================================================ */}
        {activeSection === 'settings' && (
          <div className="space-y-6">
            {/* Geocoding — moved here as a configurable process */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-emerald-600" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Geocoding (Census Bureau API — Free)</h3>
              </div>

              {/* Geocoding coverage metrics live on the Analytics tab (single
                  metrics surface) — removed here to avoid duplication. */}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Batch Size</label>
                  <select value={geocodingMaxRecords} onChange={e => setGeocodingMaxRecords(parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100">
                    <option value={1000}>1,000</option>
                    <option value={5000}>5,000</option>
                    <option value={10000}>10,000</option>
                    <option value={25000}>25,000</option>
                    <option value={50000}>50,000</option>
                    <option value={100000}>100,000</option>
                  </select>
                </div>
                <button
                  onClick={handleMarkForGeocoding}
                  disabled={markingForGeocoding || geocodingLoading}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-all font-medium mt-6"
                >
                  {markingForGeocoding ? 'Marking...' : <><MapPin className="h-4 w-4" /> Mark Pending</>}
                </button>
                <button
                  onClick={handleStartGeocoding}
                  disabled={geocodingLoading || markingForGeocoding}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 transition-all font-medium mt-6"
                >
                  {geocodingLoading ? 'Starting...' : <><Navigation className="h-4 w-4" /> Run Geocoding</>}
                </button>
              </div>

              {activeGeocodingJob && activeGeocodingJob.status === 'RUNNING' && (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Job #{activeGeocodingJob.id} Running</span>
                  </div>
                  {activeGeocodingJob.max_records && activeGeocodingJob.total_processed !== undefined && (
                    <div className="h-2 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 transition-all" style={{ width: `${Math.min(100, (activeGeocodingJob.total_processed / activeGeocodingJob.max_records) * 100)}%` }} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* API Key Status */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Database className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">API Key Status & Budgets</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                API keys are configured in the Render dashboard environment variables. Budget limits are set per waterfall job.
              </p>
              <div className="space-y-3">
                {[
                  { name: 'Brave Search API', key: 'BRAVE_SEARCH_API_KEY', cost: '$0.001/query', tier: 'Tier 2' },
                  { name: 'Hunter.io', key: 'HUNTER_API_KEY', cost: '$0.01/query', tier: 'Tier 3' },
                  { name: 'ZoomInfo', key: 'ZOOMINFO_API_KEY', cost: '$1.50/query', tier: 'Tier 4 (Future)' },
                  { name: 'Census Bureau (Geocoding)', key: 'Built-in', cost: 'Free', tier: 'Settings' },
                ].map(api => (
                  <div key={api.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{api.name}</p>
                      <p className="text-xs text-gray-500">{api.tier} · {api.cost}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${api.key === 'Built-in' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {api.key === 'Built-in' ? 'Always Available' : 'Render Env'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
interface TabProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

const Tab: React.FC<TabProps> = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
      active
        ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50 dark:bg-purple-900/20'
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`}
  >
    {icon}
    {label}
  </button>
);

interface StatCardProps {
  label: string;
  value: string;
  subValue: string;
  icon: React.ReactNode;
  color: 'teal' | 'blue' | 'purple' | 'green' | 'orange';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, subValue, icon, color }) => {
  const colorClasses = {
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">{subValue}</p>
        </div>
      </div>
    </div>
  );
};

interface FeatureItemProps {
  icon: React.ReactNode;
  label: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon, label }) => (
  <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
    <div className="text-purple-600 dark:text-purple-400">{icon}</div>
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
  </div>
);

interface QualityBarProps {
  label: string;
  percentage: number;
  color: 'green' | 'amber' | 'red';
  description: string;
}

const QualityBar: React.FC<QualityBarProps> = ({ label, percentage, color, description }) => {
  const colorClasses = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{description}</span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${colorClasses[color]}`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

interface TimeEstimateProps {
  count: number;
  time: string;
}

const TimeEstimate: React.FC<TimeEstimateProps> = ({ count, time }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
    <span className="text-sm text-gray-700 dark:text-gray-300">{count} records</span>
    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{time}</span>
  </div>
);

interface StepItemProps {
  number: number;
  title: string;
  description: string;
}

const StepItem: React.FC<StepItemProps> = ({ number, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div>
      <h4 className="font-medium text-gray-900 dark:text-gray-100">{title}</h4>
      <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  </div>
);

export default UnifiedEnrichmentManager;
